import { PostEditorEffectArgs } from '../PostEditorEffects';
import {
  PostEditorMediaAddFromExternalDeferredAction,
  PostEditorMediaOnUploadExternalCallbackAction
} from '../PostEditorActions';
import { FileWithPreview } from '../../../Publish/MediaLibrary/AddMediaSelectorModal/AddMediaSelectorModal.view';
import { MediaType } from '../../../../models/graphql';
import { ExternalMedia, SourceType } from '../../../../hooks/useMediaUpload';
import { onUploadStatusChangeFunction } from '../reducers/media.reducer';
import {
  getImageDimensions,
  getMediaFilesize,
  getMediaProportion,
  getVideoDimensions,
  isMediaIsAnimatedGif
} from '../../../../helpers/media';
import { MediaTypePostEditorState } from '../../../../models/post-editor';
import { MediaUploadStatus } from '../../../../constants/media';

export const mediaOnUploadCallbackEffect: PostEditorEffectArgs = (state, effect, dispatch) => {
  const {
    urls,
    files,
    mediaItems,
    selectedWorkspaceId,
    queueUploads,
    context,
    canvaId,
    postGroupIndex,
    customizeIndex,
    beacon
  }: PostEditorMediaOnUploadExternalCallbackAction['payload'] = effect.payload;
  const onUploadStatusChangeCallback = onUploadStatusChangeFunction(
    dispatch,
    selectedWorkspaceId,
    context,
    postGroupIndex,
    customizeIndex,
    beacon
  );

  let media: ExternalMedia[] | FileWithPreview[];

  switch (context) {
    case 'url':
      media = urls.map(url => ({
        url,
        type: MediaType.PHOTO,
        sourceType: SourceType.external,
        ...(canvaId ? { canvaId } : {})
      }));
      break;
    case 'thirdParty':
      media = mediaItems
        ? mediaItems.map(item => ({
            sourceType: SourceType.external,
            url: item.url,
            type: item.type
          }))
        : state.postGroups.flatMap(group =>
            group.groupPostAttributes.content.media.map(item => ({
              type: MediaType.PHOTO,
              url: item.url,
              sourceType: SourceType.external
            }))
          );
      break;
    case 'file':
      media = files;
      break;
    default:
      media = [];
  }

  if (media.length) {
    queueUploads(media, onUploadStatusChangeCallback);
  }
};

export const mediaWithDeferredUploadEffect: PostEditorEffectArgs = (_state, effect, dispatch) => {
  const { externalMedia }: PostEditorMediaAddFromExternalDeferredAction['payload'] = effect.payload;
  externalMedia.forEach(async (medias, postGroupIndex) => {
    for await (const media of medias) {
      let updatedMedia: MediaTypePostEditorState;

      try {
        const filesize = await getMediaFilesize(media.url);
        updatedMedia = {
          ...media,
          loading: false,
          status: MediaUploadStatus.COMPLETED,
          gif: false,
          filesize
        };
        if (media.__typename === 'Video') {
          const { width, height } = await getVideoDimensions(media.url);
          const proportions = getMediaProportion(width, height);
          updatedMedia = {
            ...updatedMedia,
            width,
            height,
            proportions
          };
        } else {
          const { width, height } = await getImageDimensions(media.url);
          const gif = await isMediaIsAnimatedGif(media.url);
          const proportions = getMediaProportion(width, height);
          updatedMedia = {
            ...updatedMedia,
            gif,
            width,
            height,
            proportions
          };
        }
      } catch (error) {
        updatedMedia = {
          ...media,
          loading: false,
          status: MediaUploadStatus.FAILED
        };
      }

      dispatch({ type: 'mediaUpdateDeferredLoaded', payload: { media: updatedMedia, postGroupIndex } });
    }
  });
};
